// A reseller's focus on one of its customers (focus.py): whose cart the page shows, sent with
// every cart write, the focus banner's "Clear focus", the reload a page gets when a write is
// refused because the focus changed since it was rendered (another tab, another device), the
// dialogs that set a focus, from "My customers" and "My Quotes" (focus_on_customer), the cart's
// switch of the focused customer's contact (choose_focus_contact, switch_focus_contact), and a
// customer's contact form, which "My customers" and those dialogs share (make_contact_form). Only
// the contact choice's Confirm (make_focus_dialog) sets a focus on a customer; on the cart, the
// focused customer's contact is switched directly (switch_focus_contact).

frappe.provide("ioi_partners")

// The element holding the cart's customer (eshop_base.html, #eshop-cart-customer).
const CART_CUSTOMER_ID = "eshop-cart-customer"
// The exception a cart write from an out-of-date page raises (focus.FocusChangedError), as the
// response names it (exc_type).
const STALE_PAGE_ERROR = "FocusChangedError"

// The customer whose cart this page shows, as the server rendered it: the expected_customer of
// every cart write (focus.check_expected_customer). "" off the e-shop pages and for a guest.
ioi_partners.expected_customer = () => {
	const holder = document.getElementById(CART_CUSTOMER_ID)
	return (holder && holder.getAttribute("data-customer")) || ""
}

// Reseller mode without a focus (focus.is_focus_required), as the server rendered it: there is no
// cart to show, and the catalogue's floating cart stays empty. False off the e-shop pages and for a
// guest.
ioi_partners.is_focus_required = () => {
	const holder = document.getElementById(CART_CUSTOMER_ID)
	return Boolean(holder && holder.hasAttribute("data-focus-required"))
}

// "Clear focus" (templates/includes/eshop_focus_banner.html): the focus goes, and the page is
// loaded again, back to the reseller's own cart; or the page the banner names instead
// (data-after-clear, /confirmation's). A failure is reported by frappe.call itself (website.js),
// and the button can be used again.
$(document).on("click", ".eshop-focus-banner__clear", (e) => {
	const $button = $(e.currentTarget)
	if ($button.prop("disabled")) {
		return
	}
	$button.prop("disabled", true)
	const after_clear = $button.attr("data-after-clear")
	frappe.call({ method: "ioi_partners.focus.clear_focus" }).then(
		() => (after_clear ? window.location.assign(after_clear) : window.location.reload()),
		() => $button.prop("disabled", false)
	)
})

// A cart write refused as out of date: the server's message is on screen (website.js shows it),
// and the page is loaded again once it is closed, with the cart the server now resolves.
$(document).ajaxComplete((event, xhr) => {
	let answer = null
	try {
		answer = JSON.parse(xhr.responseText)
	} catch (e) {
		return
	}
	if (answer && answer.exc_type === STALE_PAGE_ERROR) {
		$(document).one("hidden.bs.modal", () => window.location.reload())
	}
})

// The focus's endpoints (focus.py).
const FOCUS = "ioi_partners.focus."
// How long the form's controls may take to load (load_form_controls), in milliseconds.
const FORM_LOAD_TIMEOUT = 15000

// A call that failed. The website's frappe.call (frappe/website/js/website.js) shows the
// server's own messages (_server_messages) of any answer, a refusal's included, and with
// statusCode given, nothing else: no second "Not permitted" / "Not found" dialog. An answer
// with no message of its own (a server error, no network) gets message instead.
ioi_partners.report_call_failure = (xhr, message) => {
	let server_messages = null
	try {
		server_messages = JSON.parse(xhr.responseText)._server_messages
	} catch (e) {
		// Not a JSON answer, or none at all.
	}
	if (!server_messages) {
		frappe.msgprint(message)
	}
}

// Frappe's form controls are not part of the website's bundle: loaded once, the first time
// a form opens, as Frappe's own website pages do (templates/discussions/discussions.js).
// The website's frappe.require (frappe/website/js/website.js) calls back once the script has
// loaded, and never when it fails to: after FORM_LOAD_TIMEOUT, on_expired runs, and the page is
// as before the click; a load that ends after that opens nothing. Once loaded, it calls back at
// once.
ioi_partners.load_form_controls = (make, on_expired) => {
	let expired = false
	const timer = setTimeout(() => {
		expired = true
		on_expired()
	}, FORM_LOAD_TIMEOUT)
	frappe.require("controls.bundle.js", () => {
		clearTimeout(timer)
		if (!expired) {
			make()
		}
	})
}

// A customer's contacts' endpoints (reseller_contacts.py).
const CONTACTS = "ioi_partners.reseller_contacts."

// A customer's contact form, to add one or edit one (reseller_contacts.py), wherever a page offers it:
// "My customers" (www/my-customers.js), and the focus's contact choice, whose "Add a contact" opens it
// (add_contact) on "My customers", "My Quotes" and the cart. customer is {name, display_name}; form is the
// form's options ({salutations, lengths, languages, language}: reseller_contacts.get_contact_form_options,
// the e-shop's languages, the customer's own language, as focus.get_focus_contacts gives them); contact is
// get_customer_contact()'s answer for an edit, null for a new contact. The labels are the server's own
// (reseller_contacts.CONTACT_FIELDS), translated by the dialog. Saved, on_saved gets the server's answer and
// the form closes, never usable again: a second click cannot make the contact twice. Once closed, saved or
// not, on_hidden(saved) runs.
ioi_partners.make_contact_form = ({ customer, form, contact = null, on_saved = () => {}, on_hidden = () => {} }) => {
	const is_new = !contact
	let saving = false
	let saved = false
	const lengths = form.lengths
	const blank = { value: "", label: "" }
	const options = (list) => [blank, ...list.map((option) => ({
		value: option.value,
		// The Select control puts the label in as HTML.
		label: frappe.utils.escape_html(option.label),
	}))]
	// The e-shop's four languages; a contact's own other one is added for its form, so that
	// saving keeps it (get_customer_contact's language_label).
	let languages = form.languages
	if (contact && contact.language && !languages.some((option) => option.value === contact.language)) {
		languages = [...languages, { value: contact.language, label: contact.language_label || contact.language }]
	}

	const d = new frappe.ui.Dialog({
		title: is_new ? __("Add a contact") : __("Edit a contact"),
		fields: [
			{
				fieldname: "email", fieldtype: "Data", options: "Email", label: "Email", reqd: 1,
				length: lengths.email,
				// A login's contact keeps its address (reseller_contacts.check_email).
				read_only: contact && contact.has_login ? 1 : 0,
			},
			{ fieldname: "salutation", fieldtype: "Select", label: "Salutation", options: options(form.salutations) },
			{ fieldname: "first_name", fieldtype: "Data", label: "First name", reqd: 1, length: lengths.first_name },
			{ fieldname: "last_name", fieldtype: "Data", label: "Last name", reqd: 1, length: lengths.last_name },
			{ fieldname: "language", fieldtype: "Select", label: "Language", options: options(languages) },
			{ fieldname: "phone", fieldtype: "Data", label: "Phone" },
			{ fieldname: "mobile_no", fieldtype: "Data", label: "Mobile" },
		],
		primary_action_label: __("Save"),
		primary_action: (values) => save(values),
	})
	d.$wrapper.addClass("my-customers-dialog")
	d.$wrapper.on("hidden.bs.modal", () => {
		d.$wrapper.remove()
		on_hidden(saved)
	})

	if (is_new) {
		// The customer's language, the server's default too, when it is one of the form's.
		if (languages.some((option) => option.value === form.language)) {
			d.set_value("language", form.language)
		}
	} else {
		d.set_values(contact)
	}
	d.show()

	function save(values) {
		if (saving) {
			return
		}
		saving = true
		d.get_primary_btn().prop("disabled", true)
		const release = () => {
			saving = false
			d.get_primary_btn().prop("disabled", false)
		}

		const args = Object.assign({ customer: customer.name }, values)
		let method = CONTACTS + "create_customer_contact"
		if (!is_new) {
			method = CONTACTS + "update_customer_contact"
			args.contact = contact.name
		}

		// As My customers' customer form saves (www/my-customers.js, make_form): the form stays open with
		// what was typed when the save fails, and a refusal is reported as the server words it, once.
		frappe.call({ method, args, statusCode: {} }).then((r) => {
			if (!r || r.exc) {
				release()
				return
			}
			saved = true
			on_saved(r.message)
			d.hide()
		}, (xhr) => {
			ioi_partners.report_call_failure(xhr, __("Your changes could not be saved. Please try again."))
			release()
		})
	}
}

// The cart's switch of the focused customer's contact: its contacts asked for (get_focus_contacts), then the
// contact choice in its on_chosen mode (make_focus_dialog: "Change contact", the contact the page names
// preselected, "Add a contact" too), whose choice goes to on_chosen, with no confirmation: the page switches it
// (switch_focus_contact). customer is {name, display_name}, selected a contact's name or null. ready(open) runs
// once everything is loaded and before anything opens, so that the page may hide its own dialog first and then
// call open(); a page that does not call it is done there. on_end runs once the dialogs end without a choice
// (closed, the contacts not loaded or refused, the form's controls not loaded in time).
ioi_partners.choose_focus_contact = ({ customer, selected = null, ready, on_chosen, on_end = () => {} }) => {
	frappe.call({
		method: FOCUS + "get_focus_contacts",
		args: { customer: customer.name },
		statusCode: {},
	}).then((r) => {
		if (r && !r.exc && r.message) {
			ioi_partners.load_form_controls(
				() => ready(() => make_focus_dialog(customer, r.message, { on_end, on_chosen, selected })),
				on_end
			)
		} else {
			on_end()
		}
	}, (xhr) => {
		ioi_partners.report_call_failure(xhr, __("The contacts could not be loaded. Please try again."))
		on_end()
	})
}

// The focus's contact switched to contact, one of the focused customer's (focus.switch_focus_contact, which
// checks it all again), from a page that shows that customer's cart (expected_customer). Resolves with the
// answer ({contact, label, banner}). Rejects with "stale" when the page is out of date (FocusChangedError, read
// from exc_type: the server's message is on screen, and the page reloads once it is closed, as for every cart
// write), else with "refused", said once: the server's message, or ours when there is none. A refusal comes as a
// rejected call or as an answer carrying exc, as the contact choice's call does (make_focus_dialog's confirm).
ioi_partners.switch_focus_contact = (contact) => {
	return new Promise((resolve, reject) => {
		frappe.call({
			method: FOCUS + "switch_focus_contact",
			args: { contact, expected_customer: ioi_partners.expected_customer() },
			statusCode: {},
		}).then((r) => {
			if (!r || r.exc) {
				reject(r && r.exc_type === STALE_PAGE_ERROR ? "stale" : "refused")
				return
			}
			resolve(r.message)
		}, (xhr) => {
			let answer = null
			try {
				answer = JSON.parse(xhr.responseText)
			} catch (e) {
				// Not a JSON answer, or none at all.
			}
			if (answer && answer.exc_type === STALE_PAGE_ERROR) {
				reject("stale")
				return
			}
			ioi_partners.report_call_failure(xhr, __("The contact could not be changed. Please try again."))
			reject("refused")
		})
	})
}

// Focus on a customer, the e-shop then used for it (focus.py), wherever a page offers it: "My
// customers" (www/my-customers.js), which names no contact, and a quote's row in "My Quotes"
// (www/my-quotes.js), which names the quote's customer contact when the focus may name it. One of the
// customer's contacts is always asked for (make_focus_dialog), the one the page names preselected, or
// one added there; only the choice's Confirm sets a focus on a customer, then landing opens (on the
// cart, the focused customer's contact is switched directly instead: choose_focus_contact,
// switch_focus_contact).
// customer is {name, display_name}, contact {name, label} or null. on_end runs once the dialogs end
// without leaving the page (closed, the contacts not loaded or refused, the form's controls not
// loaded in time): the page may take its clicks again.
ioi_partners.focus_on_customer = ({ customer, contact = null, landing, on_end = () => {} }) => {
	const selected = contact ? contact.name : null
	frappe.call({
		method: FOCUS + "get_focus_contacts",
		args: { customer: customer.name },
		statusCode: {},
	}).then((r) => {
		if (r && !r.exc && r.message) {
			ioi_partners.load_form_controls(
				() => make_focus_dialog(customer, r.message, { landing, on_end, selected }),
				on_end
			)
		} else {
			on_end()
		}
	}, (xhr) => {
		ioi_partners.report_call_failure(xhr, __("The contacts could not be loaded. Please try again."))
		on_end()
	})
}

// The contact the focus names (get_focus_contacts' answer: the customer's enabled contacts with
// an email address, by label, and the contact form's options), the page's (selected) preselected
// when it is one of them, else the only one. Confirm sets a focus on the customer (set_focus, which
// checks everything again), then landing opens for it; a refusal is reported as the server words
// it, once, and the dialog stays. "Add a contact" opens the contact form instead (make_contact_form)
// once this dialog is hidden, as the contacts' dialogs of "My customers" follow each other
// (make_contacts_dialog): saved, the choice comes back with the new contact preselected (after_add);
// closed unsaved, it comes back as it was. mode is { landing, on_end, selected }; the cart's
// (choose_focus_contact) has on_chosen instead of landing: titled "Change contact", the choice (or
// the contact added) goes to on_chosen once this dialog is hidden, with no focus set and no landing.
function make_focus_dialog(customer, answer, mode) {
	const { landing, on_end, on_chosen = null, selected = null } = mode
	const contacts = answer.contacts
	let next = null
	let sending = false
	let leaving = false
	const d = new frappe.ui.Dialog({
		title: on_chosen ? __("Change contact") : __("Focus on a customer / a contact"),
		fields: [
			{
				fieldname: "contact", fieldtype: "Select", label: "Contact", reqd: 1,
				// The Select control puts the label in as HTML.
				options: [{ value: "", label: "" }, ...contacts.map((contact) => ({
					value: contact.name,
					label: frappe.utils.escape_html(contact.label),
				}))],
			},
		],
		primary_action_label: __("Confirm"),
		primary_action: (values) => {
			if (next || sending) {
				return
			}
			const contact = contacts.find((option) => option.name === values.contact)
			if (contact && on_chosen) {
				next = () => on_chosen(contact)
				d.hide()
			} else if (contact) {
				confirm(contact)
			}
		},
		secondary_action_label: __("Add a contact"),
		secondary_action: () => {
			if (next || sending) {
				return
			}
			next = () => add_contact(customer, answer, mode)
			d.hide()
		},
	})
	d.$wrapper.addClass("my-customers-dialog")
	d.$wrapper.on("hidden.bs.modal", () => {
		d.$wrapper.remove()
		if (next) {
			next()
		} else if (!leaving) {
			on_end()
		}
	})
	$("<p>").addClass("my-customers-contacts__customer").text(customer.display_name).prependTo(d.$body)
	if (selected && contacts.some((contact) => contact.name === selected)) {
		d.set_value("contact", selected)
	} else if (contacts.length === 1) {
		d.set_value("contact", contacts[0].name)
	}
	d.show()

	function confirm(contact) {
		sending = true
		d.get_primary_btn().prop("disabled", true)
		const release = () => {
			sending = false
			d.get_primary_btn().prop("disabled", false)
		}
		frappe.call({
			method: FOCUS + "set_focus",
			args: { customer: customer.name, contact: contact.name },
			statusCode: {},
		}).then((r) => {
			if (!r || r.exc) {
				release()
				return
			}
			leaving = true
			window.location.href = landing
		}, (xhr) => {
			ioi_partners.report_call_failure(xhr, __("The focus could not be set. Please try again."))
			release()
		})
	}
}

// The contact choice's "Add a contact": the customer's contact form (make_contact_form), with the
// options the choice came with. Saved, the new contact goes on (after_add); closed unsaved, the
// choice comes back from the same answer, in the same mode.
function add_contact(customer, answer, mode) {
	let added = null
	ioi_partners.make_contact_form({
		customer,
		form: answer.form,
		on_saved: (result) => {
			added = result.name
		},
		on_hidden: () => {
			if (added) {
				after_add(customer, added, mode)
			} else {
				make_focus_dialog(customer, answer, mode)
			}
		},
	})
}

// A contact just added from the choice: named as the server now lists it (get_focus_contacts, its
// label the server's), never as the form had it, preselected in the choice again, through
// focus_on_customer, or, in the cart's mode, given to on_chosen. One the focus may not name, or a
// failed call, is said with our own message every time, after the server's own when a refused call
// sent one (the website's frappe.call shows it), then the dialogs end: the contact stays added.
function after_add(customer, name, { landing, on_end, on_chosen = null }) {
	const not_selected = () => {
		frappe.msgprint(__("The contact was added, but it could not be selected. Please try again."))
		on_end()
	}
	frappe.call({
		method: FOCUS + "get_focus_contacts",
		args: { customer: customer.name },
		statusCode: {},
	}).then((r) => {
		const found = r && !r.exc && r.message && r.message.contacts.find((contact) => contact.name === name)
		if (found && on_chosen) {
			on_chosen(found)
		} else if (found) {
			ioi_partners.focus_on_customer({ customer, contact: found, landing, on_end })
		} else {
			not_selected()
		}
	}, not_selected)
}
